import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import Code, { CodeText } from '../components/Code';

const DEFAULT_WEIGHTS = { O1: 0.22, O2: 0.22, O3: 0.2, O4: 0.14, O5: 0.14, O6: 0.08 };

export default function Scoring() {
  const qc = useQueryClient();
  const spine = useQuery({ queryKey: ['spine'], queryFn: api.spine });
  const scoring = useQuery({ queryKey: ['scoring'], queryFn: api.scoring });
  const [weights, setWeights] = useState<Record<string, number>>(DEFAULT_WEIGHTS);

  const reweight = useMutation({
    mutationFn: (w: Record<string, number>) => api.scoringWithWeights(w),
    onSuccess: (data) => qc.setQueryData(['scoring'], data),
  });

  useEffect(() => {
    if (spine.data) {
      const w: Record<string, number> = {};
      for (const o of spine.data.objectives) w[o.id] = o.weight;
      setWeights(w);
    }
  }, [spine.data]);

  const sum = Object.values(weights).reduce((a, b) => a + b, 0);
  const sumValid = Math.abs(sum - 1.0) < 0.01;

  function setWeight(id: string, val: number) {
    setWeights((w) => ({ ...w, [id]: val }));
  }

  // Live recompute on slider release. Debounce a tick.
  useEffect(() => {
    if (!spine.data || !sumValid) return;
    const t = setTimeout(() => {
      reweight.mutate(weights);
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weights]);

  return (
    <div>
      <div className="ph">Spine-Anchored Scoring</div>
      <div className="h2">Alignment to objectives, vetoed by non-objectives</div>

      <div className="grid2" style={{ marginBottom: 16 }}>
        <div className="panel">
          <div className="qhead">
            <span className="qdot" style={{ background: 'var(--amber)' }} />
            Objective weights
            <span className={sumValid ? 'tag olive' : 'tag red'} style={{ marginLeft: 8 }}>
              sum {(sum * 100).toFixed(0)}%
            </span>
          </div>
          {spine.data?.objectives.map((o) => (
            <div key={o.id} style={{ marginBottom: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 14 }}>
                  <span className="tag amber" style={{ marginRight: 7 }}>{o.id}</span>
                  {o.name}
                </span>
                <span className="mono" style={{ fontSize: 14, color: 'var(--amber)', fontWeight: 600 }}>
                  {(weights[o.id] * 100 || 0).toFixed(0)}%
                </span>
              </div>
              <input
                className="slider"
                type="range"
                min="0"
                max="0.4"
                step="0.02"
                value={weights[o.id] ?? 0}
                onChange={(e) => setWeight(o.id, parseFloat(e.target.value))}
              />
            </div>
          ))}
        </div>

        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--red)' }} />Active vetoes (non-objectives)</div>
          {spine.data?.non_objectives.map((n) => (
            <div key={n.id} className="row" style={{ padding: '9px 0', borderBottom: '1px solid var(--line-soft)', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 14 }}>
                <span className="tag red" style={{ marginRight: 8 }}><Code id={n.id} kind="non_objective" compact /></span>
                {n.name}
              </span>
              <span className="mono" style={{ fontSize: 12, color: 'var(--faint)' }}>{n.veto_predicate}</span>
            </div>
          ))}
          <div className="small" style={{ marginTop: 10 }}>
            Any option tagged with a predicate above is auto-blocked, regardless of impact.
          </div>
        </div>
      </div>

      <div className="panel">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Option</th>
              <th className="center">O1</th>
              <th className="center">O2</th>
              <th className="center">O3</th>
              <th className="center">O4</th>
              <th className="center">O5</th>
              <th className="center">O6</th>
              <th className="center">Obj fit</th>
              <th className="center">Impact</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {scoring.data?.map((o) => {
              const blocked = o.status === 'BLOCKED';
              return (
                <tr key={o.id} style={blocked ? { opacity: 0.6 } : undefined}>
                  <td><Code id={o.id} kind="intervention" compact /></td>
                  <td>
                    {o.id === 'O-01' && <span style={{ color: 'var(--amber)' }}>★ </span>}
                    {o.name}
                  </td>
                  {[o.o1, o.o2, o.o3, o.o4, o.o5, o.o6].map((v, i) => (
                    <td key={i} className="center mono" style={{ color: v >= 3 ? 'var(--amber)' : v === 0 ? 'var(--faint)' : 'var(--muted)' }}>
                      {v}
                    </td>
                  ))}
                  <td className="center mono">{o.objective_fit?.toFixed(2) ?? '—'}</td>
                  <td className="center mono" style={{ fontWeight: 700, color: blocked ? 'var(--faint)' : 'var(--gold)' }}>
                    {o.impact?.toFixed(2) ?? '—'}
                  </td>
                  <td>
                    {blocked ? (
                      <span className="blockedchip">BLOCKED · <CodeText text={o.veto_hits?.join(', ') ?? ''} compact /></span>
                    ) : (
                      <span className="tag olive">Pass</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
